// Relationship-level price comparison for multi-vendor purchasing.
//
// One canonical inventory item can be bought as several vendor packages
// ("12 pouches × 12 oz" at Sysco, "2 bags × 5 lb" at Restaurant Depot). This
// module answers, for the WHOLE relationship at once:
//
//   1. Which single practical unit should every vendor package be compared in?
//   2. How much of that unit does each package contain?
//   3. Which option is the lowest trustworthy comparable price right now?
//
// The comparison unit belongs to the RELATIONSHIP, never to one option — two
// prices in different units are not comparable, so a per-option unit choice is
// a bug by construction. Packages keep their real descriptions; the unit is a
// lens, not a rewrite.
//
// Pure ESM, no Deno APIs — importable by edge functions and the Node test
// runner alike (the purchaseConversion.mjs convention). Deterministic parsing
// delegates to the Recipe Lab engine; this module never modifies it. Browser
// code does NOT import this file (the conversion table deliberately has no
// browser copy — see app/javascript/utils/countUnitModel.js): the browser
// mirror app/javascript/utils/priceComparison.js consumes the STORED results
// this module persists, and a parity test keeps the selection rule identical
// on both sides.
//
// Nothing here throws on bad input, and an unresolved quantity is null —
// never 0 — so a package we cannot interpret can never masquerade as free.

import { liftLeadingContainer, readPack } from "./packText.mjs";
import { normalizeUnit } from "../menu-recipe-costing/units.mjs";
import { vendorIsRetired } from "./vendorState.mjs";
import {
  convertBasisQuantity,
  hasPackageBasisConflict,
  packageBasisFingerprint,
  sameComparisonUnit,
  validateComparisonAgainstBasis,
} from "./packageBasis.mjs";
import {
  buildMeasurementProfile,
  mergeMeasurementProfile,
  readMeasurementProfile,
} from "./measurementProfile.mjs";

// Pack-text reading is shared with purchaseConversion.mjs — one string must
// never mean two things (see packText.mjs). Re-exported under this module's
// historical name for callers and tests.
export { liftLeadingContainer as normalizePackForComparison };

// ── Constants ────────────────────────────────────────────────────────────────

// Below this confidence an AI-estimated comparison is still SHOWN, but it can
// no longer crown an option "lowest price" — selection falls back to the
// preferred vendor and the UI says the comparison needs review.
export const COMPARISON_AI_CONFIDENCE_THRESHOLD = 0.65;

// v3 adds package_basis_fingerprint: the identity of the order package this
// comparison measured. A reader whose current package fingerprint differs
// must treat the snapshot as stale — it describes a package that no longer
// exists on the row. v2 snapshots (no fingerprint) are validated against the
// current package where stored data can prove agreement.
export const PRICE_COMPARISON_VERSION = 3;

export const COMPARISON_SOURCES = Object.freeze([
  "deterministic",
  "ai_estimate",
  "manual_override",
]);

// A comparable price older than this may not silently beat a fresh one.
export const COMPARISON_STALE_AFTER_DAYS = 90;

// Normalization rule version, baked into persisted bridge-fact keys. Bump
// whenever a conversion rule changes meaning (display-factor tables, the
// pack reader, coercion guards, the density/ambiguity policy): facts written
// under an older version stop matching and the observation re-resolves once
// under the new rules instead of silently mixing arithmetics.
export const NORMALIZATION_RULE_VERSION = 2;

// Display units the relationship may compare in, per family, with factors to
// the engine's canonical bases (g / ml / each). Milliliters, liters, kilograms
// and grams are INPUT units only — they are never a display unit, and
// container words (case, box, bag, pail…) are never a comparison unit unless
// every package is the objectively identical container (handled separately).
const MASS_DISPLAY_UNITS = Object.freeze({ lb: 453.59237, oz: 28.349523125 });
const VOLUME_DISPLAY_UNITS = Object.freeze({ gal: 3785.411784, qt: 946.352946, "fl oz": 29.5735295625 });
const COUNT_DISPLAY_UNIT = "each";

// A bare "oz" is dimensionally ambiguous. The package reader deliberately
// keeps it as weight because pack text alone cannot know whether a 14 oz can
// holds tomatoes or tonic. Price comparison also receives the PRODUCT,
// though, and named liquids settle the ambiguity without estimating anything:
// "12 x 46 oz cans" of Pineapple Juice is 552 fluid ounces. Container words
// alone never trigger this rule, so canned solids remain weight.
const UNAMBIGUOUS_FLUID_OUNCE_PRODUCT_RE = /(?:\bjuice\b|\btonic(?:\s+water)?\b|\bclub\s+soda\b|\bsoda\s+water\b|\bseltzer\b|\b(?:sparkling|mineral|spring|drinking|purified)\s+water\b|\blemonade\b|\blimeade\b)/i;

function bareOunceIsFluidVolume(pack, option, item) {
  if (pack?.kind !== "mass" || pack?.unitNorm?.ambiguousOz !== true) return false;
  const productText = [item?.description, option?.vendor_description]
    .map((value) => String(value ?? "").trim())
    .filter(Boolean)
    .join(" | ");
  if (UNAMBIGUOUS_FLUID_OUNCE_PRODUCT_RE.test(productText)) return true;
  // Packaged liquid whipping cream uses fluid-ounce labels. The product
  // settles the bare "oz" ambiguity; no density conversion is involved.
  // Solid/powder cream products and explicit net-weight labels stay mass.
  const liquidCream = /\b(?:heavy\s+(?:whipping\s+)?cream|whipping\s+cream)\b/i.test(productText);
  const nonLiquidOrWeight = /\b(?:powder(?:ed)?|dry|dried|cheese|sour|ice|whipped|topping|soup)\b|\bnet\s*(?:wt|weight)\b/i
    .test(`${productText} | ${option?.pack_description || ''}`);
  return liquidCream && !nonLiquidOrWeight;
}

// ── Stated-unit conversion (group comparison completeness) ──────────────────
// The group AI may express a package's contents in the unit its label STATES
// ("2 x 14.1 oz jars" in a lb group → 28.2 with stated_unit 'oz') and leave
// the arithmetic to code. These factors extend the display-unit tables above
// with the metric and minor input units a label may state — same canonical
// bases (g / ml), same numbers, one owner. Same-dimension ONLY by contract:
// a converter that crossed mass↔volume would be a silent density guess, and
// densities are the AI's job, labeled, never code's.
const STATED_MASS_FACTORS = Object.freeze({
  ...MASS_DISPLAY_UNITS, // lb, oz
  g: 1, gram: 1, grams: 1,
  kg: 1000, kilogram: 1000, kilograms: 1000,
});
const STATED_VOLUME_FACTORS = Object.freeze({
  ...VOLUME_DISPLAY_UNITS, // gal, qt, "fl oz"
  pt: 473.176473,
  cup: 236.5882365, cups: 236.5882365,
  ml: 1, milliliter: 1, milliliters: 1, millilitre: 1, millilitres: 1,
  l: 1000, liter: 1000, liters: 1000, litre: 1000, litres: 1000,
});

const hasUnit = (table, key) => Object.prototype.hasOwnProperty.call(table, key);

/** 'mass' | 'volume' for a known stated unit; null for anything else
 *  (piece words, container words, garbage). */
export function statedUnitDimension(unit) {
  const key = String(unit ?? "").trim().toLowerCase();
  if (!key) return null;
  if (hasUnit(STATED_MASS_FACTORS, key)) return "mass";
  if (hasUnit(STATED_VOLUME_FACTORS, key)) return "volume";
  return null;
}

/**
 * Convert a quantity between two stated units of ONE dimension (mass or
 * volume). Returns the converted number, or null when either unit is unknown
 * or the two units live in different dimensions — cross-dimension conversion
 * (lb ↔ fl oz) is never performed here.
 */
export function convertStatedQuantity(quantity, fromUnit, toUnit) {
  const n = Number(quantity);
  if (!Number.isFinite(n)) return null;
  const from = String(fromUnit ?? "").trim().toLowerCase();
  const to = String(toUnit ?? "").trim().toLowerCase();
  const table = hasUnit(STATED_MASS_FACTORS, from) && hasUnit(STATED_MASS_FACTORS, to)
    ? STATED_MASS_FACTORS
    : hasUnit(STATED_VOLUME_FACTORS, from) && hasUnit(STATED_VOLUME_FACTORS, to)
      ? STATED_VOLUME_FACTORS
      : null;
  if (!table) return null;
  return (n * table[from]) / table[to];
}

// Words that signal a pounds/gallons/quarts mention inside a pack description,
// used by the unit-preference rules ("prefer a useful unit already present in
// at least one vendor's pack description").
const LB_WORDS = /(\d|\b)(lb|lbs|pound|pounds)\b|#\s*\d/i;
const GAL_WORDS = /\b(gal|gallon|gallons)\b/i;
const QT_WORDS = /\b(qt|qts|quart|quarts)\b/i;

function positiveNumber(v) {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
}

// Parses below this confidence (container-only phrases, unknown units,
// #10-can shapes) stay unresolved: guessing a quantity here would let a wrong
// number pick a vendor.
const MIN_PARSE_CONFIDENCE = 0.8;

/**
 * Deterministically measure ONE option's package.
 *
 * @returns {{ resolved: boolean, family: 'mass'|'volume'|'count'|null,
 *             baseQty: number|null, containerWord: string|null,
 *             containerCount: number|null, perContainerBaseQty: number|null,
 *             pieceWord: string|null, mentionsText: string,
 *             parseConfidence: number, reason: string|null }}
 */
// The size of one piece, when the pack text or the extracted weight/volume
// states it, in the engine's base for its family. This is NEVER a package
// measurement: it is the second fact needed to check a counted case against a
// weight ("72 pieces" x "2 oz" = 9 lb, not 18). Both texts are read by
// packText — the one pack reader — and nothing here parses anything itself.
function readPiecemealSize(pack, option, item) {
  if (pack?.perPiece?.qty > 0 && pack.unitNorm?.factor > 0 && pack.kind) {
    if (bareOunceIsFluidVolume(pack, option, item)) {
      return { family: 'volume', baseQty: pack.perPiece.qty * VOLUME_DISPLAY_UNITS['fl oz'] };
    }
    return { family: pack.kind, baseQty: pack.perPiece.qty * pack.unitNorm.factor };
  }
  for (const text of [option?.weight_volume, item?.weight_volume]) {
    if (!text) continue;
    const piece = readPack(text);
    // Only a bare, single-piece size counts: "2 OZ" states a piece, while
    // "6/10 LB CS" states a case and would silently become a piece size.
    if (!piece.read || piece.packCount !== 1 || !(piece.unitQty > 0)) continue;
    if (!piece.unitNorm?.factor || piece.kind === "count") continue;
    return { family: piece.kind, baseQty: piece.unitQty * piece.unitNorm.factor };
  }
  return null;
}

/**
 * A purchase unit that is itself a weight or volume measure ("lb", "LBS",
 * "gal") — the vendor bills that measure. Returns its family and the factor
 * from one unit to the engine's base (g / ml), or null for a wrapper, a
 * piece word, a count, or nothing.
 */
function billedPurchaseUnit(option) {
  const raw = String(option?.purchase_unit ?? "").trim();
  if (!raw || /\d/.test(raw)) return null;
  const norm = normalizeUnit(raw);
  if ((norm.kind !== "mass" && norm.kind !== "volume") || !(norm.factor > 0)) return null;
  return { kind: norm.kind, factor: norm.factor, key: norm.key };
}

export function measureOptionPackage(option, item = {}) {
  const sourceText = option?.pack_description ?? item?.pack_size ?? "";
  const pack = readPack(sourceText);
  const out = {
    resolved: false, family: null, baseQty: null,
    containerWord: pack.containerWord, containerPosition: pack.containerPosition,
    containerCount: null, perContainerBaseQty: null,
    pieceWord: pack.pieceWord,
    // Carried for the count-to-weight cross-check; no existing consumer reads
    // these, and they never take part in choosing a comparison unit.
    pieceCount: pack.pieceCount ?? null,
    piecemealSize: readPiecemealSize(pack, option, item),
    mentionsText: String(sourceText || ""), parseConfidence: pack.confidence, reason: null,
  };
  if (!pack.read) {
    // ── Purchase-unit identity ("LB", "GAL") over an unreadable pack ───────
    // The count converter's path 1a already rules that a mass/volume PURCHASE
    // unit says what the vendor bills: one package IS one pound. A line
    // printed "18 LBS @ $13.50" carries the unit and the rate and no pack
    // text at all, so the reader has nothing to contradict; measuring it as
    // one unit of its own billing measure is the same rule this engine's
    // sibling applies, and it keeps a candidate's 18 lb wheel (store HiHo,
    // 2026-09-06: $13.50 ÷ 18 = "$0.75/lb") from ever standing in for the
    // source's own weight. A readable pack keeps today's reading untouched.
    const billed = billedPurchaseUnit(option);
    if (billed) {
      return {
        ...out,
        resolved: true,
        family: billed.kind,
        baseQty: billed.factor,
        containerCount: null,
        perContainerBaseQty: billed.factor,
        parseConfidence: 1,
        evidence: "purchase_unit_identity",
      };
    }
    return { ...out, reason: pack.reason };
  }
  if (pack.confidence < MIN_PARSE_CONFIDENCE) {
    return { ...out, reason: "low_parse_confidence" };
  }

  // normalizeUnit("oz") correctly reports mass when no product context is
  // available. For a named liquid, reinterpret that exact written amount in
  // the volume base (ml); no density bridge or guessed size is involved.
  const fluidOunce = bareOunceIsFluidVolume(pack, option, item);
  const family = fluidOunce ? "volume" : pack.kind;
  const factor = fluidOunce ? VOLUME_DISPLAY_UNITS["fl oz"] : pack.unitNorm.factor;
  const per = pack.unitQty * factor;
  const baseQty = pack.packCount * per;
  if (!(baseQty > 0)) return { ...out, reason: "bad_pack_quantity" };

  return {
    ...out,
    resolved: true,
    family,
    baseQty,
    containerCount: pack.packCount > 1 ? pack.packCount : (pack.containerWord ? pack.packCount : null),
    perContainerBaseQty: per,
  };
}

// ── Relationship unit choice ─────────────────────────────────────────────────

function chooseMassUnit(measures) {
  if (measures.some((m) => LB_WORDS.test(m.mentionsText))) return "lb";
  // Inner-package scale, not case total: a 12 oz pouch compares in oz even
  // when the case holds nine pounds.
  const refs = measures.map((m) => m.perContainerBaseQty ?? m.baseQty);
  return refs.every((r) => r >= MASS_DISPLAY_UNITS.lb) ? "lb" : "oz";
}

function chooseVolumeUnit(measures) {
  if (measures.some((m) => GAL_WORDS.test(m.mentionsText))) return "gal";
  const refs = measures.map((m) => m.perContainerBaseQty ?? m.baseQty);
  if (refs.every((r) => r >= VOLUME_DISPLAY_UNITS.gal)) return "gal";
  if (measures.some((m) => QT_WORDS.test(m.mentionsText))) return "qt";
  if (refs.every((r) => r >= VOLUME_DISPLAY_UNITS.qt)) return "qt";
  return "fl oz";
}

export function displayFactor(family, unit) {
  if (family === "mass") return MASS_DISPLAY_UNITS[unit] ?? null;
  if (family === "volume") return VOLUME_DISPLAY_UNITS[unit] ?? null;
  if (family === "count") return 1;
  return null;
}

// "$0.49/apple" is what a buyer asked for; "$0.49/each" is what a schema
// wants. When every package counts the same thing, say the thing.
function sharedPieceWord(measures) {
  const first = measures[0]?.pieceWord;
  if (!first) return null;
  return measures.every((m) => m.pieceWord === first) ? first : null;
}

/** The display unit one family's measures compare in, and how to express them. */
function familyDisplay(family, measures) {
  if (family === "mass") {
    const unit = chooseMassUnit(measures);
    return { unit, label: unit, quantityOf: (m) => m.baseQty / MASS_DISPLAY_UNITS[unit] };
  }
  if (family === "volume") {
    const unit = chooseVolumeUnit(measures);
    return { unit, label: unit, quantityOf: (m) => m.baseQty / VOLUME_DISPLAY_UNITS[unit] };
  }
  const unit = sharedPieceWord(measures) ?? COUNT_DISPLAY_UNIT;
  return { unit, label: unit, quantityOf: (m) => m.baseQty };
}

// Which family speaks for a relationship the parser could only read in part:
// the one most packages landed in, and on a tie the one a food buyer compares
// in. Weight beats volume beats piece count — "price per pound" is the answer
// to almost every produce and protein question.
const FAMILY_PREFERENCE = ["mass", "volume", "count"];

function dominantFamily(measures) {
  const counts = new Map();
  for (const m of measures) counts.set(m.family, (counts.get(m.family) ?? 0) + 1);
  let best = null;
  for (const family of FAMILY_PREFERENCE) {
    const n = counts.get(family) ?? 0;
    if (n > 0 && (best == null || n > counts.get(best))) best = family;
  }
  return best;
}

/**
 * What the parser DID read when it could not read everything.
 *
 * Returning a bare "unresolved" threw away real measurements — the AI bridging
 * an 88-count apple case was never told that the other vendor's case of the
 * same apples is 40 lb, which is the single most useful fact in the request.
 * This block travels with the unresolved result so the prompt can state it.
 * It is deliberately NOT a resolution: a comparison covering some of the
 * vendors would crown a "lowest price" among a subset, which is a lie.
 */
function buildPartialFromMeasures(measureList) {
  const resolved = measureList.filter((m) => m.resolved);
  if (resolved.length === 0) return null;

  const family = dominantFamily(resolved);
  const inFamily = resolved.filter((m) => m.family === family);
  const chosen = familyDisplay(family, inFamily);

  return {
    comparisonUnit: chosen.unit,
    comparisonUnitLabel: chosen.label,
    unitFamily: family,
    options: inFamily.map((m) => ({
      optionId: m.optionId,
      quantityPerPurchasePackage: chosen.quantityOf(m),
    })),
    // Every package the parser could read at all, each in its own family's
    // natural unit — including the ones outside the chosen family, which is
    // exactly what the bridge question needs.
    measures: resolved.map((m) => {
      const own = familyDisplay(m.family, [m]);
      return {
        optionId: m.optionId,
        unitFamily: m.family,
        quantity: own.quantityOf(m),
        unit: own.label,
      };
    }),
  };
}

// ── Stored measurement profiles as a comparison source ──────────────────────
// A package the parser cannot read may still carry a durable measurement
// profile (written by the import's combined identity+measurement pass, a
// previous relationship AI resolution, or a human). When every active option
// resolves — deterministically or through a current profile — into one
// family, the comparison is ARITHMETIC and no AI request is needed. The
// relationship AI remains the fallback for what stays genuinely unresolved.

// Factors to the engine's canonical bases (g / ml / each) for the units a
// profile may store. 'pt' converts here even though it is never CHOSEN as a
// display unit.
const PROFILE_UNIT_FACTORS = {
  mass: { lb: MASS_DISPLAY_UNITS.lb, oz: MASS_DISPLAY_UNITS.oz },
  volume: { ...VOLUME_DISPLAY_UNITS, pt: 473.176473, qt: 946.352946 },
};

function profileBaseQty(profile) {
  if (profile.family === "count") return profile.quantityPerPackage;
  const factor = PROFILE_UNIT_FACTORS[profile.family]?.[profile.unit];
  return factor ? profile.quantityPerPackage * factor : null;
}

// Entry source vocabulary for a profile-backed entry: a human fact is a
// manual override, an exact reading is deterministic, an estimate says so.
function profileEntrySource(profile) {
  if (profile.source === "user") return "manual_override";
  if (profile.source === "explicit" || profile.source === "deterministic") return "deterministic";
  return "ai_estimate";
}

/**
 * Resolve the relationship from deterministic measures plus stored,
 * still-current measurement profiles — no AI. Returns a resolution fragment
 * (comparisonUnit/label/family/source/confidence/reasoning/options/
 * unresolvedOptionIds) or null when any option lacks both a deterministic
 * measure and a usable profile in one shared family.
 */
function resolveFromStoredProfiles(active, measures) {
  // Per option, the facts available in each family.
  const facts = [];
  for (const option of active) {
    const m = measures.get(option.id);
    const profile = readMeasurementProfile(option);
    const byFamily = new Map();
    if (m?.resolved) {
      byFamily.set(m.family, { kind: "deterministic", baseQty: m.baseQty, measure: m });
    }
    if (profile) {
      const baseQty = profileBaseQty(profile);
      if (baseQty != null && baseQty > 0 && !byFamily.has(profile.family)) {
        byFamily.set(profile.family, { kind: "profile", baseQty, profile });
      }
    }
    if (byFamily.size === 0) return null;
    facts.push({ option, byFamily });
  }

  // First family (in buyer preference order) every option can speak in.
  const family = FAMILY_PREFERENCE.find((f) => facts.every((entry) => entry.byFamily.has(f)));
  if (!family) return null;
  // Nothing new to say when the parser already resolved everything — the
  // deterministic pass owns that outcome.
  if (facts.every((entry) => entry.byFamily.get(family).kind === "deterministic")) return null;

  // Display unit: reuse the family rules over pseudo-measures (pack-text
  // mentions still steer lb/gal preference); count compares per shared
  // profile unit word when unanimous, else 'each'.
  let unit;
  if (family === "count") {
    const units = new Set(facts.map((entry) => {
      const fact = entry.byFamily.get(family);
      return fact.kind === "profile" ? fact.profile.unit : (fact.measure.pieceWord ?? COUNT_DISPLAY_UNIT);
    }));
    unit = units.size === 1 ? [...units][0] : COUNT_DISPLAY_UNIT;
  } else {
    const pseudo = facts.map((entry) => {
      const fact = entry.byFamily.get(family);
      return {
        baseQty: fact.baseQty,
        perContainerBaseQty: fact.kind === "deterministic" ? fact.measure.perContainerBaseQty : null,
        mentionsText: fact.kind === "deterministic"
          ? fact.measure.mentionsText
          : String(entry.option.pack_description ?? ""),
      };
    });
    unit = family === "mass" ? chooseMassUnit(pseudo) : chooseVolumeUnit(pseudo);
  }
  const factor = displayFactor(family, unit);
  if (factor == null) return null;

  const entries = [];
  let sawEstimate = false;
  let confidence = 1;
  for (const { option, byFamily } of facts) {
    const fact = byFamily.get(family);
    if (fact.kind === "deterministic") {
      entries.push(optionEntry(option, {
        quantity: fact.baseQty / factor, source: "deterministic", confidence: 1,
      }));
      continue;
    }
    const source = profileEntrySource(fact.profile);
    const entryConfidence = source === "manual_override"
      ? 1
      : (Number.isFinite(Number(fact.profile.confidence)) ? Number(fact.profile.confidence) : null);
    if (source === "ai_estimate") sawEstimate = true;
    if (entryConfidence != null) confidence = Math.min(confidence, entryConfidence);
    entries.push(optionEntry(option, {
      quantity: fact.baseQty / factor,
      source,
      confidence: entryConfidence,
      assumption: fact.profile.reasoning ?? null,
      estimated: source === "ai_estimate",
    }));
  }

  return {
    comparisonUnit: unit,
    comparisonUnitLabel: unit,
    unitFamily: family,
    source: sawEstimate ? "ai_estimate" : "deterministic",
    confidence,
    reasoning: "Every package resolves through its stored measurement profile; compared without a new AI request.",
    options: entries,
    unresolvedOptionIds: [],
  };
}

const CONTAINER_SIZE_TOLERANCE = 0.000001;

// A labelled metric size and its US-customary twin ("355 mL" and "12 fl oz",
// "330 mL" and "11.15 fl oz") are one bottle written two ways; the rounding
// between them is under half a percent.
const LABELLED_EQUIVALENT_TOLERANCE = 0.005;

function sameContainerAcross(measures) {
  const first = measures[0];
  // In "12 x 1 qt case", the case is the outer selling wrapper, not one of
  // the twelve measured quarts. Comparing two such packs per "case" would
  // silently label 12 quarts as 12 cases. Keep the physical family unit.
  if (measures.some((m) => m.containerWord === "case" && m.containerPosition === "per"
    && m.containerCount > 1)) return null;
  if (!first?.containerWord || first.perContainerBaseQty == null) {
    return sameCountedContainerAcross(measures);
  }
  const word = first.containerWord;
  for (const m of measures) {
    if (m.containerWord !== word) return sameCountedContainerAcross(measures);
    if (m.perContainerBaseQty == null) return null;
    const scale = Math.max(m.perContainerBaseQty, first.perContainerBaseQty, 1);
    if (Math.abs(m.perContainerBaseQty - first.perContainerBaseQty) > CONTAINER_SIZE_TOLERANCE * scale) {
      return null;
    }
  }
  return word;
}

// Multipacks of the same-size unit where only some packages NAME the unit:
// an invoice's "355ml x 24" beside a listing's "12 fl oz bottle; 24 ct" is a
// case of the same bottles on both sides, and a buyer compares them per
// bottle (with the case counts explained), not per fluid ounce. Every
// package must count more than one inner unit of one size within the
// labelled-equivalent tolerance, and every package that does name the unit
// must name the same word. Single wrappers ("50 lb" beside "50 lb bag") keep
// the family unit, exactly as before.
function sameCountedContainerAcross(measures) {
  let word = null;
  const first = measures[0];
  if (measures.some((m) => m.containerWord === "case" && m.containerPosition === "per"
    && m.containerCount > 1)) return null;
  if (!first || !(first.containerCount > 1) || !(first.perContainerBaseQty > 0)) return null;
  for (const m of measures) {
    if (!(m.containerCount > 1) || !(m.perContainerBaseQty > 0)) return null;
    if (m.containerWord) {
      if (word && m.containerWord !== word) return null;
      word = m.containerWord;
    }
    const scale = Math.max(m.perContainerBaseQty, first.perContainerBaseQty, 1);
    if (Math.abs(m.perContainerBaseQty - first.perContainerBaseQty) > LABELLED_EQUIVALENT_TOLERANCE * scale) {
      return null;
    }
  }
  return word;
}

// ── Snapshot codec ───────────────────────────────────────────────────────────

/**
 * Read an option's stored comparison: the v2 price_comparison snapshot first,
 * else either historical ai_objective shape, normalized to one structure.
 * Returns null when nothing usable is stored.
 */
export function readPriceComparison(option) {
  const snap = option?.conversion_snapshot;
  if (!snap || typeof snap !== "object") return null;

  const v2 = snap.price_comparison;
  if (v2 && typeof v2 === "object" && positiveNumber(v2.quantity_per_purchase_package)) {
    return {
      version: Number(v2.version) || 2,
      relationshipUnit: v2.relationship_unit ?? null,
      unitFamily: v2.unit_family ?? null,
      quantityPerPurchasePackage: Number(v2.quantity_per_purchase_package),
      source: COMPARISON_SOURCES.includes(v2.source) ? v2.source : "ai_estimate",
      confidence: Number.isFinite(Number(v2.confidence)) ? Number(v2.confidence) : null,
      assumption: v2.assumption ?? null,
      reasoning: v2.reasoning ?? null,
      verified: v2.verified === true,
      calculatedAt: v2.calculated_at ?? null,
      // v3: the fingerprint of the order package this comparison measured.
      // Absent on legacy snapshots; readers validate those against the
      // current package instead of trusting them blind.
      packageBasisFingerprint: v2.package_basis_fingerprint ?? null,
      // The AI's own account of HOW it got the quantity — 'explicit',
      // 'derived_from_pack', 'ai_inferred_standard_pack',
      // 'ai_inferred_contextual'. This, not who wrote the row, is what the
      // Est mark describes.
      dataQuality: v2.data_quality ?? null,
    };
  }

  // Legacy ai_objective, both historical shapes. Its unit was chosen per
  // option, so it may not match the relationship unit — callers must check.
  const ai = snap.ai_objective;
  if (ai && typeof ai === "object") {
    const qty = positiveNumber(ai.qty_per_objective_unit);
    const unit = ai.objective_unit ?? ai.unit ?? null;
    if (qty && unit) {
      return {
        version: 1,
        relationshipUnit: String(unit),
        unitFamily: null,
        quantityPerPurchasePackage: qty,
        source: "ai_estimate",
        confidence: Number.isFinite(Number(ai.confidence)) ? Number(ai.confidence) : null,
        assumption: null,
        reasoning: ai.reasoning ?? null,
        verified: false,
        calculatedAt: null,
      };
    }
    const directCost = positiveNumber(ai.cost_per_unit ?? ai.price);
    if (directCost && (ai.unit || ai.objective_unit)) {
      return {
        version: 1,
        relationshipUnit: String(ai.unit ?? ai.objective_unit),
        unitFamily: null,
        quantityPerPurchasePackage: null,
        source: "ai_estimate",
        confidence: Number.isFinite(Number(ai.confidence)) ? Number(ai.confidence) : null,
        assumption: null,
        reasoning: ai.reasoning ?? null,
        verified: false,
        calculatedAt: null,
        comparableUnitCostOverride: directCost,
      };
    }
  }
  return null;
}

/** Build the v3 snapshot value stored under conversion_snapshot.price_comparison. */
export function buildPriceComparisonSnapshot({
  relationshipUnit, unitFamily, quantityPerPurchasePackage, source,
  confidence = null, assumption = null, reasoning = null, verified = false,
  calculatedAt = null, packageBasisFingerprint: fingerprint = null,
}) {
  return {
    version: PRICE_COMPARISON_VERSION,
    relationship_unit: relationshipUnit,
    unit_family: unitFamily,
    quantity_per_purchase_package: quantityPerPurchasePackage,
    source,
    confidence,
    assumption,
    reasoning,
    verified: verified === true,
    calculated_at: calculatedAt,
    // The order package this comparison measured, at measurement time. A
    // later package-defining change (unit, contents, inner structure) makes
    // the row's fingerprint differ, and every reader then refuses this
    // snapshot instead of decorating the new package with the old package's
    // price-per-unit.
    package_basis_fingerprint: fingerprint,
  };
}

// ── Relationship resolution ──────────────────────────────────────────────────

function optionEntry(option, { quantity, source, confidence, assumption = null, estimated = false }) {
  const price = positiveNumber(option.package_price);
  return {
    optionId: option.id,
    quantityPerPurchasePackage: quantity,
    // Unrounded on purpose: the stored numeric(12,6) columns round at write
    // time, and a rounded tie must never hide a real winner.
    comparableUnitCost: price != null && quantity != null ? price / quantity : null,
    source,
    confidence,
    assumption,
    estimated: estimated === true,
  };
}

function activeOf(options) {
  return (Array.isArray(options) ? options : []).filter(
    (o) => o && o.status !== "inactive" && o.id != null,
  );
}

/**
 * Split import/invoice comparison upkeep by its authoritative owner.
 *
 * A confirmed Equivalent group owns ONE denominator and its member snapshots.
 * Running the older per-item writer over one member afterwards can replace a
 * volume snapshot with a mass snapshot (the Pineapple Juice repeat-import
 * failure) or replace an AI bridge with a piece count. Ungrouped items still
 * belong to the per-item writer.
 */
export function partitionPerItemComparisonTargets(itemIds, relationshipRows = []) {
  const ids = [...new Set((Array.isArray(itemIds) ? itemIds : [])
    .map(Number).filter((id) => Number.isFinite(id) && id > 0))];
  const targetKeys = new Set(ids.map(String));
  const groupedKeys = new Set();
  for (const row of Array.isArray(relationshipRows) ? relationshipRows : []) {
    if (row?.relationship_type !== "same_item" || row?.status !== "confirmed") continue;
    const a = String(row?.item_a_id ?? "");
    const b = String(row?.item_b_id ?? "");
    if (targetKeys.has(a)) groupedKeys.add(a);
    if (targetKeys.has(b)) groupedKeys.add(b);
  }
  return {
    individualItemIds: ids.filter((id) => !groupedKeys.has(String(id))),
    groupedItemIds: ids.filter((id) => groupedKeys.has(String(id))),
  };
}

/** Load the confirmed Equivalent edges touching a set, then partition it. */
export async function loadPerItemComparisonTargets(serviceClient, {
  storeId, itemIds,
} = {}) {
  const ids = [...new Set((Array.isArray(itemIds) ? itemIds : [])
    .map(Number).filter((id) => Number.isFinite(id) && id > 0))];
  if (!serviceClient || storeId == null || ids.length === 0) {
    return { individualItemIds: ids, groupedItemIds: [] };
  }
  const readEdges = (column) => serviceClient
    .from("item_relationships")
    .select("item_a_id,item_b_id,relationship_type,status")
    .eq("store_id", storeId)
    .eq("relationship_type", "same_item")
    .eq("status", "confirmed")
    .in(column, ids);
  const [left, right] = await Promise.all([readEdges("item_a_id"), readEdges("item_b_id")]);
  if (left?.error) throw left.error;
  if (right?.error) throw right.error;
  return partitionPerItemComparisonTargets(ids, [...(left?.data || []), ...(right?.data || [])]);
}

/**
 * Resolve the relationship's comparison unit and every active option's
 * quantity + unrounded comparable cost in it.
 *
 * Precedence: a manual override on the item wins; then a validated AI
 * resolution passed by the caller; then the deterministic pass; otherwise the
 * relationship is 'unresolved' (which is an answer, not an error — the UI
 * says the comparison needs review and selection falls back to preferred).
 *
 * An unresolved deterministic result carries `partial` whenever the parser
 * measured at least one package: the unit those packages share, their
 * quantities in it, and every package's own measurement. Callers must not
 * treat it as a comparison — a "lowest price" over a subset of the vendors is
 * a lie — but the AI bridge is built from it, which is the difference between
 * asking "how much is in an 88-count apple case?" and asking it while knowing
 * the same apples ship 40 lb to the case from another vendor.
 *
 * @param {object} item     inventory item row (comparison_* columns honored)
 * @param {Array}  options  purchase_options rows
 * @param {object} [opts]   { aiResolution } — pre-validated AI result:
 *   { comparisonUnit, comparisonUnitLabel, unitFamily, confidence, reasoning,
 *     options: [{ optionId, quantityPerPurchasePackage, assumption, confidence }] }
 */
// A counted case and a weight are two facts about one package, and the second
// one has to survive the first. When the parser counted the pieces and the
// package also states a piece size, the only weight that package can have is
// count x piece size: a 72-piece case of 2 oz patties is 9 lb, and an estimate
// of 18 lb is not a near miss, it is a different package. An estimate that
// contradicts the arithmetic is refused rather than corrected — the option
// stays unresolved, which every caller already knows how to show.
export const COUNT_BRIDGE_TOLERANCE = 0.05;

function countBridgeContradicts(measure, family, quantity, factor) {
  if (!measure?.resolved || measure.family !== "count") return false;
  if (!(measure.baseQty > 0) || !(factor > 0) || !family || family === "count") return false;
  const piece = measure.piecemealSize;
  if (!piece || piece.family !== family || !(piece.baseQty > 0)) return false;
  const impliedBase = measure.baseQty * piece.baseQty;
  const claimedBase = quantity * factor;
  if (!(impliedBase > 0) || !(claimedBase > 0)) return false;
  return Math.abs(impliedBase - claimedBase)
    > Math.max(impliedBase, claimedBase) * COUNT_BRIDGE_TOLERANCE;
}

export function resolveRelationshipComparison(item = {}, options = [], { aiResolution = null } = {}) {
  const active = activeOf(options);
  const base = {
    comparisonUnit: null,
    comparisonUnitLabel: null,
    unitFamily: null,
    source: "unresolved",
    confidence: null,
    reasoning: null,
    options: [],
    unresolvedOptionIds: active.map((o) => o.id),
  };
  if (active.length === 0) return { ...base, unresolvedOptionIds: [] };

  const measures = new Map(active.map((o) => [o.id, { ...measureOptionPackage(o, item), optionId: o.id }]));

  // ── Manual override: the human said which unit this relationship compares in.
  if (item?.comparison_unit_source === "manual_override" && item?.comparison_unit) {
    const unit = String(item.comparison_unit);
    const family = item.comparison_unit_family
      ?? (MASS_DISPLAY_UNITS[unit] ? "mass" : VOLUME_DISPLAY_UNITS[unit] ? "volume" : "count");
    const factor = displayFactor(family, unit);
    const entries = [];
    const unresolved = [];
    for (const option of active) {
      const stored = readPriceComparison(option);
      if (stored?.source === "manual_override" && stored.relationshipUnit === unit
        && positiveNumber(stored.quantityPerPurchasePackage)) {
        entries.push(optionEntry(option, {
          quantity: stored.quantityPerPurchasePackage,
          source: "manual_override",
          confidence: 1,
          estimated: false,
        }));
        continue;
      }
      const m = measures.get(option.id);
      if (factor != null && m?.resolved && m.family === family) {
        entries.push(optionEntry(option, {
          quantity: m.baseQty / factor, source: "deterministic", confidence: 1,
        }));
        continue;
      }
      if (stored && stored.relationshipUnit === unit && positiveNumber(stored.quantityPerPurchasePackage)) {
        entries.push(optionEntry(option, {
          quantity: stored.quantityPerPurchasePackage,
          source: stored.source,
          confidence: stored.confidence,
          assumption: stored.assumption,
          estimated: stored.source === "ai_estimate",
        }));
        continue;
      }
      unresolved.push(option.id);
    }
    return {
      ...base,
      comparisonUnit: unit,
      comparisonUnitLabel: unit,
      unitFamily: family,
      source: "manual_override",
      confidence: 1,
      reasoning: item.comparison_unit_reasoning ?? "Comparison unit set manually.",
      options: entries,
      unresolvedOptionIds: unresolved,
    };
  }

  // ── Validated AI resolution supplied by the caller.
  if (aiResolution && aiResolution.comparisonUnit) {
    const family = aiResolution.unitFamily ?? null;
    const factor = displayFactor(family, aiResolution.comparisonUnit);
    const byId = new Map((aiResolution.options || []).map((r) => [String(r.optionId), r]));
    const entries = [];
    const unresolved = [];
    for (const option of active) {
      const m = measures.get(option.id);
      // A deterministic measure in the same family outranks the estimate for
      // that option — the AI chose the unit, the parser supplies exact math.
      if (factor != null && m?.resolved && m.family === family) {
        entries.push(optionEntry(option, {
          quantity: m.baseQty / factor, source: "deterministic", confidence: 1,
        }));
        continue;
      }
      const r = byId.get(String(option.id));
      const qty = positiveNumber(r?.quantityPerPurchasePackage);
      // The same-family branch above did not fire, so the parser and the
      // estimate are speaking about different dimensions. Where the parser
      // counted the pieces AND the package states a piece size, that
      // arithmetic outranks an estimate that disagrees with it.
      if (qty && !countBridgeContradicts(m, family, qty, factor)) {
        entries.push(optionEntry(option, {
          quantity: qty,
          source: "ai_estimate",
          confidence: Number.isFinite(Number(r.confidence)) ? Number(r.confidence) : aiResolution.confidence ?? null,
          assumption: r.assumption ?? null,
          estimated: true,
        }));
        continue;
      }
      unresolved.push(option.id);
    }
    return {
      ...base,
      comparisonUnit: aiResolution.comparisonUnit,
      comparisonUnitLabel: aiResolution.comparisonUnitLabel ?? aiResolution.comparisonUnit,
      unitFamily: family,
      source: "ai_estimate",
      confidence: Number.isFinite(Number(aiResolution.confidence)) ? Number(aiResolution.confidence) : null,
      reasoning: aiResolution.reasoning ?? null,
      options: entries,
      unresolvedOptionIds: unresolved,
    };
  }

  // ── Deterministic pass.
  const measureList = active.map((o) => measures.get(o.id));
  const family = measureList[0].family;
  const allResolved = measureList.every((m) => m.resolved);
  const oneFamily = allResolved && measureList.every((m) => m.family === family);

  if (!allResolved || !oneFamily) {
    // Stored measurement profiles first: what a previous AI pass, an import's
    // combined identity+measurement pass, or a human already established
    // about these packages resolves the relationship arithmetically. Only
    // what stays genuinely unresolved falls through to the AI bridge.
    const fromProfiles = resolveFromStoredProfiles(active, measures);
    if (fromProfiles) return { ...base, ...fromProfiles };

    // Unresolved is an answer, not an error — but it now carries what WAS
    // read, so the AI bridge argues from measurements instead of from nothing.
    const partial = buildPartialFromMeasures(measureList);
    const out = {
      ...base,
      reasoning: allResolved
        ? "Packages measure in different families (weight vs volume vs pieces) — needs a practical bridge."
        : "One or more packages could not be interpreted deterministically.",
      options: [],
      unresolvedOptionIds: allResolved
        ? active.map((o) => o.id)
        : active.filter((o) => !measures.get(o.id).resolved).map((o) => o.id),
    };
    return partial ? { ...out, partial } : out;
  }

  // Identical containers (same word, same size) compare per container — the
  // way a bar manager actually talks about same-size bottles or cans.
  let unit;
  let label;
  let quantityOf;
  const containerWord = active.length > 1 ? sameContainerAcross(measureList) : null;
  if (containerWord && family !== "count") {
    unit = containerWord;
    label = containerWord;
    quantityOf = (m) => m.containerCount ?? m.baseQty / m.perContainerBaseQty;
  } else {
    const chosen = familyDisplay(family, measureList);
    unit = chosen.unit;
    label = chosen.label;
    quantityOf = chosen.quantityOf;
  }

  return {
    ...base,
    comparisonUnit: unit,
    comparisonUnitLabel: label,
    unitFamily: family,
    source: "deterministic",
    confidence: 1,
    reasoning: containerWord && family !== "count"
      ? `Every package is the same ${containerWord}; compared per ${containerWord}.`
      : `All packages convert to ${label}.`,
    options: active.map((o) => optionEntry(o, {
      quantity: quantityOf(measures.get(o.id)), source: "deterministic", confidence: 1,
    })),
    unresolvedOptionIds: [],
  };
}

// ── Automatic selection ──────────────────────────────────────────────────────

function isComparisonPriceStale(option, now) {
  const t = Date.parse(option?.effective_date);
  if (!Number.isFinite(t)) return true;
  return now - t > COMPARISON_STALE_AFTER_DAYS * 24 * 60 * 60 * 1000;
}

function eligibleForPurchase(option) {
  return !!option
    && option.status === "active"
    && option.is_approved === true
    && !option.is_hidden_from_allocation
    // A retired vendor never wins the automatic selection — its package may
    // still describe stock on the shelf, but it is not a way to BUY.
    && !vendorIsRetired(option)
    && positiveNumber(option.package_price) != null
    // A package whose stored facts conflict has no single orderable package
    // to win with. Entries for such options are already dropped at assembly;
    // this guards callers that hand in a comparison assembled elsewhere.
    && !hasPackageBasisConflict(option);
}

/**
 * The automatic winner: lowest trustworthy UNROUNDED comparable price.
 *
 * Eligibility: purchasable (active, approved, not hidden, positively priced)
 * AND resolved into the relationship unit AND — when the entry is an AI
 * estimate — confident enough to act on. Stale prices lose to fresh ones; when
 * every candidate is stale the cheapest may still win, flagged usedStale so
 * the UI says the price may be outdated. Exact ties break preferred → newest
 * verified effective date → stable id order, and are reported as ties rather
 * than pretending one vendor is uniquely cheaper.
 *
 * @returns {{ optionId: string|null,
 *             reason: 'best_price'|'best_price_ai_estimate'|'tied_best_price'|'unresolved',
 *             tiedOptionIds: string[], usedStale: boolean, confidence: number|null }}
 */
export function selectAutomaticOption({ options = [], comparison = null, now = Date.now() } = {}) {
  const none = { optionId: null, reason: "unresolved", tiedOptionIds: [], usedStale: false, confidence: null };
  if (!comparison || !comparison.comparisonUnit || !Array.isArray(comparison.options)) return none;

  const entryById = new Map(comparison.options.map((e) => [String(e.optionId), e]));
  const candidates = [];
  for (const option of activeOf(options)) {
    if (!eligibleForPurchase(option)) continue;
    const entry = entryById.get(String(option.id));
    if (!entry || positiveNumber(entry.quantityPerPurchasePackage) == null) continue;
    if (entry.comparableUnitCost == null) continue;
    if (entry.source === "ai_estimate") {
      const conf = Number(entry.confidence);
      if (!Number.isFinite(conf) || conf < COMPARISON_AI_CONFIDENCE_THRESHOLD) continue;
    }
    candidates.push({ option, entry, stale: isComparisonPriceStale(option, now) });
  }
  if (candidates.length === 0) return none;

  const fresh = candidates.filter((c) => !c.stale);
  const pool = fresh.length > 0 ? fresh : candidates;
  const usedStale = fresh.length === 0;

  let bestCost = Infinity;
  for (const c of pool) if (c.entry.comparableUnitCost < bestCost) bestCost = c.entry.comparableUnitCost;
  const tied = pool.filter((c) => c.entry.comparableUnitCost === bestCost);

  let winner = tied[0];
  let reason = "best_price";
  if (tied.length > 1) {
    reason = "tied_best_price";
    const preferred = tied.find((c) => c.option.is_preferred === true);
    if (preferred) {
      winner = preferred;
    } else {
      const verified = tied
        .filter((c) => c.option.verification_status === "verified" && Number.isFinite(Date.parse(c.option.effective_date)))
        .sort((a, b) => Date.parse(b.option.effective_date) - Date.parse(a.option.effective_date));
      winner = verified[0]
        ?? [...tied].sort((a, b) => String(a.option.id).localeCompare(String(b.option.id)))[0];
    }
  } else if (winner.entry.source === "ai_estimate") {
    reason = "best_price_ai_estimate";
  }

  return {
    optionId: String(winner.option.id),
    reason,
    tiedOptionIds: tied.length > 1 ? tied.map((c) => String(c.option.id)) : [],
    usedStale,
    confidence: winner.entry.confidence ?? null,
  };
}

// ── Stored-data assembly (what the browser mirror also implements) ───────────

/**
 * Rebuild a comparison from STORED data only — item.comparison_* columns plus
 * each option's price_comparison snapshot (legacy ai_objective accepted when
 * its unit happens to match). No parsing: this is exactly what the browser
 * can do offline, and the parity test pins both sides to it.
 */
const INFERRED_DATA_QUALITIES = new Set(["ai_inferred_standard_pack", "ai_inferred_contextual"]);

/**
 * Was the comparison quantity INFERRED, as opposed to read or calculated from
 * the package? Drives the quiet "Est" disclosure and nothing else — an
 * inferred quantity is exactly as eligible to win on price as an explicit one.
 */
function isInferredQuantity(stored, verdict) {
  if (!stored) return false;
  if (stored.dataQuality) return INFERRED_DATA_QUALITIES.has(stored.dataQuality);
  return stored.source === "ai_estimate" && verdict?.proven !== true;
}

/**
 * Express a stored snapshot's quantity in the group's comparison unit.
 * Returns { quantity, reason } — quantity null with a machine-readable reason
 * when the snapshot cannot reach that unit at all.
 */
function quantityInComparisonUnit(stored, unit) {
  if (!stored) return { quantity: null, reason: "no_stored_comparison" };
  const storedQty = positiveNumber(stored.quantityPerPurchasePackage);
  if (storedQty == null) return { quantity: null, reason: "no_stored_quantity" };
  if (sameComparisonUnit(stored.relationshipUnit, unit)) return { quantity: storedQty, reason: null };
  const converted = positiveNumber(convertBasisQuantity(storedQty, stored.relationshipUnit, unit));
  if (converted != null) return { quantity: converted, reason: null };
  return { quantity: null, reason: "unit_not_convertible" };
}

export function assembleStoredComparison(item = {}, options = [], context = {}) {
  const active = activeOf(options);
  // A pooled EQUIVALENT group hands in options belonging to SEVERAL items.
  // A package basis (and the fingerprint stamped on its snapshot) is a fact
  // about the option's OWN item — resolving a sibling's option against the
  // canonical item recomputes a different fingerprint and silently discards a
  // perfectly good comparison. `itemsById` lets the caller supply the owning
  // item per option; without it every option keeps using `item`, unchanged.
  const itemsById = context?.itemsById || {};
  const itemFor = (option) => itemsById[String(option?.inventory_item_id)] || item;
  // `comparisonUnit` lets the group layer impose ONE denominator on every
  // member (equivalent_group_comparisons.comparison_unit) instead of each
  // member's own column disagreeing with its siblings'.
  let unit = context?.comparisonUnit ?? item?.comparison_unit ?? null;
  let inferred = false;
  if (!unit) {
    // Callers that only hold option rows (allocation needs, count lines) can
    // still compare: every v2 snapshot in one relationship was written with
    // the SAME relationship_unit, so a unanimous unit is authoritative.
    // Version-1 (legacy ai_objective) units were chosen per option and never
    // qualify.
    // Unanimity is about the physical unit, not its spelling: "gal" and
    // "gallon" are one answer written twice.
    const units = [];
    for (const option of active) {
      const stored = readPriceComparison(option);
      if (stored?.version >= 2 && stored.relationshipUnit
        && !units.some((seen) => sameComparisonUnit(seen, stored.relationshipUnit))) {
        units.push(stored.relationshipUnit);
      }
    }
    if (units.length === 1) {
      unit = units[0];
      inferred = true;
    }
  }
  if (!unit) {
    return {
      comparisonUnit: null, comparisonUnitLabel: null, unitFamily: null,
      source: "unresolved", confidence: null, reasoning: null,
      options: [], unresolvedOptionIds: active.map((o) => o.id),
    };
  }
  const entries = [];
  const unresolved = [];
  const unresolvedDetails = [];
  for (const option of active) {
    const stored = readPriceComparison(option);
    const ownItem = itemFor(option);
    // Reach the group's denominator from whatever the snapshot was written
    // in: the same physical unit spelled differently needs no arithmetic, and
    // a sibling unit in the same family converts exactly. Only a genuinely
    // different quantity (a weight against a volume) fails to arrive.
    const { quantity, reason: reachReason } = quantityInComparisonUnit(stored, unit);
    // A stored comparison speaks only for the package it measured. An option
    // whose package facts conflict has no agreed package; a snapshot whose
    // fingerprint no longer matches, or whose quantity provably disagrees
    // with the current package, is stale. Those land in unresolved — they
    // must not rank, select, or badge. A snapshot that can be neither proven
    // nor disproven (an AI bridge, an unresolved order-side conversion)
    // stays, flagged basisVerified:false so titles require proof.
    const conflicted = quantity != null && hasPackageBasisConflict(option, { item: ownItem });
    const verdict = quantity != null && !conflicted
      ? validateComparisonAgainstBasis(option, stored, { item: ownItem })
      : null;
    if (verdict && verdict.usable) {
      entries.push({
        ...optionEntry(option, {
          quantity,
          source: stored.source,
          confidence: stored.confidence,
          assumption: stored.assumption,
          // "Estimated" describes the QUANTITY, not who touched the record.
          // "1000 ct box" is 1000 each whoever wrote it down; a 140-count case
          // of lemons weighing ~40 lb is a genuine inference. The AI states
          // which it did in data_quality, so that is what the mark reads.
          // Legacy snapshots carry no data_quality: fall back to the source
          // label, softened by proof — a quantity the deterministic parser can
          // confirm from the pack text was not estimated by anyone.
          estimated: isInferredQuantity(stored, verdict),
        }),
        basisVerified: verdict.proven,
      });
    } else {
      unresolved.push(option.id);
      // Never a bare blank: every option that cannot be compared carries the
      // reason it could not, so the surface can say what needs confirming.
      unresolvedDetails.push({
        optionId: option.id,
        reason: conflicted ? "package_basis_conflict" : (verdict?.reason ?? reachReason),
      });
    }
  }
  // An inferred unit takes its provenance from the snapshots themselves: the
  // weakest entry speaks for the relationship.
  let source = COMPARISON_SOURCES.includes(item.comparison_unit_source) ? item.comparison_unit_source : "unresolved";
  let confidence = Number.isFinite(Number(item.comparison_unit_confidence)) ? Number(item.comparison_unit_confidence) : null;
  if (inferred && entries.length > 0) {
    source = entries.some((e) => e.source === "ai_estimate") ? "ai_estimate" : "deterministic";
    const confidences = entries.map((e) => Number(e.confidence)).filter((c) => Number.isFinite(c));
    confidence = confidences.length ? Math.min(...confidences) : null;
  }
  return {
    comparisonUnit: unit,
    comparisonUnitLabel: unit,
    unitFamily: item.comparison_unit_family ?? null,
    source,
    confidence,
    reasoning: item.comparison_unit_reasoning ?? null,
    options: entries,
    unresolvedOptionIds: unresolved,
    unresolvedDetails,
  };
}

// ── Server-side persistence orchestrator ─────────────────────────────────────

function isMissingSchemaError(error) {
  const code = error?.code ?? "";
  return code === "42703" || code === "PGRST204" || code === "PGRST205";
}

/**
 * Recompute and persist one relationship's comparison. Deterministic first;
 * when packages stay unresolved and an aiCaller is provided, ONE AI call
 * covers the whole relationship. Persists per-option snapshots (MERGED into
 * conversion_snapshot — sibling keys like ai_objective and
 * package_interpretation are preserved) and the item's comparison_* columns.
 *
 * Non-fatal by contract: returns the resolution (or null) and NEVER throws —
 * an import or invoice must not fail because a comparison could not be
 * stored. Missing columns (migration not yet applied) degrade silently.
 *
 * @param {object} serviceClient supabase client (service role or RLS)
 * @param {object} args { storeId, item, options, aiCaller, now }
 *   aiCaller: async ({ item, options, deterministic }) => validated
 *   aiResolution | null (the caller owns prompt/validation).
 */
export async function applyRelationshipComparison(serviceClient, {
  storeId, item, options, aiCaller = null, now = Date.now(),
} = {}) {
  try {
    if (!serviceClient || !item?.id) return null;
    // A manual override is authoritative until explicitly cleared — never
    // recomputed away by imports, invoices, or AI passes.
    if (item.comparison_unit_source === "manual_override") {
      return resolveRelationshipComparison(item, options);
    }

    let resolution = resolveRelationshipComparison(item, options);
    if (resolution.source === "unresolved" && resolution.unresolvedOptionIds.length > 0 && aiCaller) {
      try {
        const aiResolution = await aiCaller({ item, options, deterministic: resolution });
        if (aiResolution) {
          resolution = resolveRelationshipComparison(item, options, { aiResolution });
        }
      } catch {
        // AI unavailability leaves the relationship unresolved, not broken.
      }
    }

    const calculatedAt = new Date(now).toISOString();

    if (resolution.comparisonUnit) {
      for (const entry of resolution.options) {
        const option = (options || []).find((o) => String(o?.id) === String(entry.optionId));
        if (!option) continue;
        const snapshot = buildPriceComparisonSnapshot({
          relationshipUnit: resolution.comparisonUnit,
          unitFamily: resolution.unitFamily,
          quantityPerPurchasePackage: entry.quantityPerPurchasePackage,
          source: entry.source,
          confidence: entry.confidence,
          assumption: entry.assumption,
          reasoning: resolution.reasoning,
          calculatedAt,
          // Stamp the package this comparison measured. A later material
          // package change moves the row's fingerprint and every reader then
          // refuses this snapshot until the next recompute.
          packageBasisFingerprint: packageBasisFingerprint(option, { item }),
        });
        const existing = option.conversion_snapshot && typeof option.conversion_snapshot === "object"
          ? option.conversion_snapshot
          : {};
        // An AI-estimated quantity also becomes the option's durable
        // measurement profile, so the NEXT recompute resolves arithmetically
        // instead of re-asking the model. mergeMeasurementProfile enforces
        // provenance: it never overwrites a user/verified profile and only
        // upgrades an estimate on stronger evidence.
        let nextSnapshot = { ...existing, price_comparison: snapshot };
        if (entry.source === "ai_estimate" && entry.quantityPerPurchasePackage != null) {
          const profile = buildMeasurementProfile({
            family: resolution.unitFamily,
            unit: resolution.comparisonUnit,
            quantityPerPackage: entry.quantityPerPurchasePackage,
            source: "ai_estimate",
            confidence: entry.confidence,
            reasoning: entry.assumption ?? resolution.reasoning ?? null,
            option,
            calculatedAt,
          });
          const merged = profile ? mergeMeasurementProfile(nextSnapshot, profile, option) : null;
          if (merged) nextSnapshot = merged;
        }
        try {
          const { error } = await serviceClient
            .from("purchase_options")
            .update({ conversion_snapshot: nextSnapshot })
            .eq("id", option.id);
          if (error && !isMissingSchemaError(error)) throw error;
        } catch {
          // Per-option persistence is best-effort.
        }
      }
    }

    try {
      const patch = {
        comparison_unit: resolution.comparisonUnit,
        comparison_unit_family: resolution.unitFamily,
        comparison_unit_source: resolution.comparisonUnit ? resolution.source : null,
        comparison_unit_confidence: resolution.confidence,
        comparison_unit_reasoning: resolution.reasoning,
        comparison_unit_updated_at: calculatedAt,
      };
      let query = serviceClient.from("inventory_items").update(patch).eq("id", item.id);
      if (storeId != null) query = query.eq("store_id", storeId);
      const { error } = await query;
      if (error && !isMissingSchemaError(error)) throw error;
    } catch {
      // Item-level metadata is additive; its absence only disables display.
    }

    return resolution;
  } catch {
    return null;
  }
}
